import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

export default class extends Controller {
  static targets = [
    "enableToursCheckbox",
    "enableQrKeyCheckbox",
    "enableAmenityReservationsCheckbox",
    "amenityReservationsFeeStrategy",
    "anonymousNote",
    "virtualKeysSection",
    "companySelect",
    "activityFeedPhotos",
    "smartLockDefaultAccessGroupCheckbox",
    "amazonKeyForBusinessCheckbox",
    "submitButton",
    "licensePlateEnabled",
    "licensePlateSection",
    "nameField",
    "zipCodeField",
    "countryField",
    "stateField",
    "frontDeskStationEnabled",
    "visitorLogsEnabled",
    "visitorPassInstructionsSection"
  ]
  static values = {
    tenantPrivacyLegislationAlert: String,
    tenantPhotosActivityFeedAlert: String,
    companyPhotosActivityFeedAlert: String,
    tenantSendInvitationsAlert: String,
    syncWithCloudKeyzAlert: String,
    syncWithDormakabaAlert: String,
    syncWithKwiksetAlert: String,
    syncWithYaleAlert: String,
    disableAmazonKeyForBusinessConfirmTitle: String,
    disableAmazonKeyForBusinessConfirmBody: String,
    changeNameConfirmTitle: String,
    changeNameConfirmBody: String,
    changeAddressConfirmTitle: String,
    changeAddressConfirmBody: String,
    changeNameAndAddressConfirmTitle: String,
    changeNameAndAddressConfirmBody: String,
  }

  connect() {
    const feeStrategySelect = this.hasAmenityReservationsFeeStrategyTarget && this.amenityReservationsFeeStrategyTarget.querySelector("select")
    this.feeStrategyOriginallyDisabled = feeStrategySelect && feeStrategySelect.disabled
    this.toggleAmenitySettings()
    this.toggleVirtualKeysSettings()
    this.updateLicensePlateVisibility()
    this.toggleVisitorLogsCheckbox()
    this.amazonKeyForBusinessOriginalState = this
      .hasAmazonKeyForBusinessCheckboxTarget
      ? this.amazonKeyForBusinessCheckboxTarget.checked
      : null
    this.nameOriginalValue = this.hasNameFieldTarget
      ? this.nameFieldTarget.value
      : null
    this.zipCodeOriginalValue = this.hasZipCodeFieldTarget
      ? this.zipCodeFieldTarget.value
      : null
    this.countryOriginalValue = this.hasCountryFieldTarget
      ? this.countryFieldTarget.value
      : null
    this.stateOriginalValue = this.hasStateFieldTarget
      ? this.stateFieldTarget.value
      : null

    this.element.addEventListener(
      "turbo-confirm:cancel",
      this.handleConfirmCancel.bind(this),
    )
    this.element.addEventListener(
      "country-select:connected",
      this.captureStateOriginalValue.bind(this),
    )
  }

  // The state field's options (and value) are populated by the country-select
  // controller after this controller connects, so its true original value can
  // only be captured once country-select has finished its own initial connect.
  captureStateOriginalValue() {
    if (this.hasStateFieldTarget) {
      this.stateOriginalValue = this.stateFieldTarget.value
    }
  }

  handleConfirmCancel() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
    }
  }

  async updateActivityFeedPhotos(event) {
    const companyId = this.companySelectTarget.value
    if (!companyId) return

    const response = await get(`/companies/${companyId}`, {
      responseKind: "json",
    })
    const company = await response.json

    if (company.activity_feed_photos_enabled === false) {
      this.activityFeedPhotosTarget.checked = false
      this.showTenantPhotosActivityFeedAlert(event)
    } else {
      this.activityFeedPhotosTarget.checked = true
    }
  }

  changeTourCheckbox() {
    if (
      this.enableToursCheckboxTarget.checked &&
      !this.enableQrKeyCheckboxTarget.checked
    ) {
      if (
        confirm(
          "Self Guided Touring requires visitor passes. Enable Visitor Passes?",
        )
      ) {
        this.enableQrKeyCheckboxTarget.checked = true
      } else {
        this.enableToursCheckboxTarget.checked = false
      }
    }
  }

  showTenantPrivacyLegislationAlert(event) {
    if (event.target.checked == true) {
      if (confirm(this.tenantPrivacyLegislationAlertValue)) {
        event.target.checked = true
      } else {
        event.target.checked = false
      }
    }
  }

  showTenantPhotosActivityFeedAlert(event) {
    // User is being alerted when photosActivityFeed is unchecked or when company without activity feed is assigned to building
    if (event.target.checked == false) {
      this.handleAlertMessage(event, this.tenantPhotosActivityFeedAlertValue)
    } else if (this.activityFeedPhotosTarget.checked == false) {
      this.handleAlertMessage(event, this.companyPhotosActivityFeedAlertValue)
    }
  }

  handleAlertMessage(event, alertMessage) {
    if (confirm(alertMessage)) {
      event.target.checked = false
    } else {
      event.target.checked = true
    }
  }

  showSendInvitationsAlert(event) {
    if (event.target.checked == false) {
      if (confirm(this.tenantSendInvitationsAlertValue)) {
        event.target.checked = false
      } else {
        event.target.checked = true
      }
    }
  }

  toggleAmenitySettings() {
    if (!this.hasAmenityReservationsFeeStrategyTarget) return
    if (!this.hasEnableAmenityReservationsCheckboxTarget) return
    if (this.feeStrategyOriginallyDisabled) return

    this.amenityReservationsFeeStrategyTarget.hidden = !this.enableAmenityReservationsCheckboxTarget.checked
  }

  toggleVirtualKeysSettings() {
    this.virtualKeysSectionTargets.forEach(section => {
      section.hidden = !this.enableQrKeyCheckboxTarget.checked
    })
  }

  showSyncWithCloudKeyzAlert(event) {
    if (event.target.checked == true) {
      if (confirm(this.syncWithCloudKeyzAlertValue)) {
        event.target.checked = true
      } else {
        event.target.checked = false
      }
    }
  }

  showSyncWithDormakabaAlert(event) {
    if (event.target.checked) {
      event.target.checked = confirm(this.syncWithDormakabaAlertValue)
      if (event.target.checked) this.enableSmartLockDefaultAccessGroup()
    }
  }

  showSyncWithYaleAlert(event) {
    if (event.target.checked) {
      event.target.checked = confirm(this.syncWithYaleAlertValue)
      if (event.target.checked) this.enableSmartLockDefaultAccessGroup()
    }
  }

  showSyncWithKwiksetAlert(event) {
    if (event.target.checked) {
      event.target.checked = confirm(this.syncWithKwiksetAlertValue)
      if (event.target.checked) this.enableSmartLockDefaultAccessGroup()
    }
  }

  enableSmartLockDefaultAccessGroup() {
    if (this.hasSmartLockDefaultAccessGroupCheckboxTarget) {
      this.smartLockDefaultAccessGroupCheckboxTarget.checked = true
    }
  }

  toggleAnonymousNote(event) {
    this.anonymousNoteTarget.classList.toggle(
      "hidden",
      event.target.value !== "anonymous",
    )
  }

  toggleVisitorPassInstructions(event) {
    const custom = event.target.value === "true"

    this.visitorPassInstructionsSectionTarget.classList.toggle("hidden", !custom)
  }

  updateLicensePlateVisibility() {
    if (!this.hasLicensePlateEnabledTarget) return

    this.licensePlateSectionTargets.forEach(section => {
      section.hidden = !this.licensePlateEnabledTarget.checked
    })
  }

  toggleVisitorLogsCheckbox() {
    if (!this.hasFrontDeskStationEnabledTarget || !this.hasVisitorLogsEnabledTarget) return

    this.visitorLogsEnabledTarget.hidden = !this.frontDeskStationEnabledTarget.checked
  }

  updateAmazonKeyForBusinessConfirmation() {
    if (
      !this.hasSubmitButtonTarget ||
      !this.hasAmazonKeyForBusinessCheckboxTarget
    ) {
      return
    }

    const wasOriginallyEnabled =
      this.amazonKeyForBusinessOriginalState === true
    const isNowDisabled = !this.amazonKeyForBusinessCheckboxTarget.checked

    if (wasOriginallyEnabled && isNowDisabled) {
      this.submitButtonTarget.dataset.turboConfirm =
        this.disableAmazonKeyForBusinessConfirmBodyValue
      this.submitButtonTarget.dataset.confirmTitle =
        this.disableAmazonKeyForBusinessConfirmTitleValue
      this.submitButtonTarget.dataset.confirmAccept = "Disable and save"
    } else {
      delete this.submitButtonTarget.dataset.turboConfirm
      delete this.submitButtonTarget.dataset.confirmTitle
      delete this.submitButtonTarget.dataset.confirmAccept
    }
  }

  updateHandleChangeConfirmation() {
    if (!this.hasSubmitButtonTarget) return

    const nameChanged =
      this.hasNameFieldTarget &&
      this.nameFieldTarget.value !== this.nameOriginalValue
    const zipCodeChanged =
      this.hasZipCodeFieldTarget &&
      this.zipCodeFieldTarget.value !== this.zipCodeOriginalValue
    const countryChanged =
      this.hasCountryFieldTarget &&
      this.countryFieldTarget.value !== this.countryOriginalValue
    const stateChanged =
      this.hasStateFieldTarget &&
      this.stateFieldTarget.value !== this.stateOriginalValue
    const addressChanged = zipCodeChanged || countryChanged || stateChanged

    let title, body
    if (nameChanged && addressChanged) {
      title = this.changeNameAndAddressConfirmTitleValue
      body = this.changeNameAndAddressConfirmBodyValue
    } else if (nameChanged) {
      title = this.changeNameConfirmTitleValue
      body = this.changeNameConfirmBodyValue
    } else if (addressChanged) {
      title = this.changeAddressConfirmTitleValue
      body = this.changeAddressConfirmBodyValue
    }

    if (title) {
      this.submitButtonTarget.dataset.turboConfirm = body
      this.submitButtonTarget.dataset.confirmTitle = title
      this.submitButtonTarget.dataset.confirmAccept = "Change and save"
    } else {
      delete this.submitButtonTarget.dataset.turboConfirm
      delete this.submitButtonTarget.dataset.confirmTitle
      delete this.submitButtonTarget.dataset.confirmAccept
    }
  }
}
